import { useState, useEffect, useCallback, useRef } from "react";
import api from "../services/api";
import BANK_VAT from "../config/bankVatDefaults.json";
import { useAuth } from "../contexts/AuthContext";
import BankAllocationModal from "../components/BankAllocationModal";
import BankAttachModal from "../components/BankAttachModal";
import BankSuggestionsModal from "../components/BankSuggestionsModal";
import BankRemisesModal, { BankRemiseDetail } from "../components/BankRemisesModal";
import ExpenseReimburseModal from "../components/ExpenseReimburseModal";
import AccountPicker from "../components/AccountPicker";
import { bankLineGroups } from "../utils/accountGroups";
import DocBadge from "../components/DocBadge";

const TABS = ["Mes banques", "Vue consolidée", "Transactions", "Rapprochement", "Import relevé", "Alertes & SCA"];
const IMPORT_TAB = 4;
// Onglets alimentés par une connexion bancaire (Bridge, désactivé) : masqués tant qu'aucune banque n'est connectée
const CONNECTED_TABS = [0, 1, 2, 3, 5];

function fmtEur(n) { return (n ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"; }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString("fr-FR") : "—"; }

// TVA d'une ligne sans facture (même règle que server/utils/bankLineVat.js) :
// débit en attente, non rapproché, vers une charge (6) ou une immobilisation (2).
// Taux de la ligne, sinon taux par défaut du compte. null si non applicable.
function lineVat(row) {
  const acc = String(row.user_override_account || row.category_account || "471");
  if (!(row.amount < 0) || row.status !== "pending" || row.reconciled_invoice_id || row.reconciled_purchase_invoice_id) return null;
  if (!BANK_VAT.eligiblePrefixes.some(p => acc.startsWith(p))) return null;
  const def = BANK_VAT.zeroRatePrefixes.some(p => acc.startsWith(p)) ? 0 : BANK_VAT.defaultRate;
  const rate = row.vat_rate ?? def;
  const ttc = Math.round(Math.abs(row.amount) * 100) / 100;
  const tva = rate > 0 ? Math.round(ttc * rate / (100 + rate) * 100) / 100 : 0;
  const noteKey = Object.keys(BANK_VAT.notes || {}).find(p => acc.startsWith(p));
  return { rate, def, isDefault: row.vat_rate == null, ttc, tva, ht: Math.round((ttc - tva) * 100) / 100, vatAccount: acc.startsWith("2") ? "44562" : "44566", note: noteKey ? BANK_VAT.notes[noteKey] : null };
}

// Débit en attente qui part en charge (6xx) alors qu'un achat du même montant
// est déjà comptabilisé (same_amount_purchase, calculé par le serveur)
function chargeOnPaidPurchase(row) {
  if (!row.same_amount_purchase || row.status !== "pending" || row.reconciled_purchase_invoice_id) return false;
  return String(row.user_override_account || row.category_account || "471").startsWith("6");
}

// Écart entre l'opération et la facture suggérée (suggestions jusqu'à 45 jours) :
// « , datée du 05/09/2026 (12 j avant le paiement) » ; "" si même jour ou date inconnue
function suggestionGap(row) {
  const d = row.suggested_invoice_date || row.suggested_purchase_date;
  if (!d || !row.date) return "";
  const days = Math.round((new Date(String(row.date).slice(0, 10)) - new Date(String(d).slice(0, 10))) / 86400000);
  if (!days) return "";
  return `, datée du ${fmtDate(d)} (${Math.abs(days)} j ${days > 0 ? "avant" : "après"} le paiement)`;
}

const STATUS_LABEL = {
  "connecté":       { label: "Connecté",       color: "#16a34a", bg: "#dcfce7" },
  "erreur":         { label: "Erreur",          color: "#dc2626", bg: "#fee2e2" },
  "action_requise": { label: "Action requise",  color: "#d97706", bg: "#fef3c7" },
  "en_attente":     { label: "En attente",      color: "#6b7280", bg: "#f3f4f6" },
  "déconnecté":     { label: "Déconnecté",      color: "#9ca3af", bg: "#f9fafb" },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || STATUS_LABEL["en_attente"];
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
      color: s.color, background: s.bg }}>
      {s.label}
    </span>
  );
}

function BankInitials({ name }) {
  const code = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 3).toUpperCase();
  return (
    <div style={{ width: 40, height: 40, borderRadius: 10, background: "#e6f1fb",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 12, fontWeight: 800, color: "#185fa5", flexShrink: 0 }}>
      {code}
    </div>
  );
}

// ── Onglet 1 : Mes banques ────────────────────────────────────────
function TabMesBanques({ reload }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [syncing, setSyncing]         = useState(null);
  const [showModal, setShowModal]     = useState(false);
  const [banks, setBanks]             = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [msg, setMsg]                 = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ connections: c }, b] = await Promise.all([
        api.banking.connections(),
        api.banking.banks(),
      ]);
      setConnections(c || []);
      setBanks(b || []);
    } catch { setConnections([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async (connId) => {
    setSyncing(connId); setMsg("");
    try {
      const r = await api.banking.sync(connId);
      setMsg(`Sync OK — ${r.transactions_synced} transaction(s) importée(s).`);
      reload(); load();
    } catch (e) {
      setMsg(e?.message || "Erreur de synchronisation.");
    } finally { setSyncing(null); }
  };

  const handleDisconnect = async (connId, bankName) => {
    if (!window.confirm(`Déconnecter ${bankName} ?`)) return;
    await api.banking.disconnect(connId).catch(() => {});
    load(); reload();
  };

  const handleConnect = async () => {
    if (!selectedBank) return;
    try {
      const r = await api.banking.initiate({ bank_id: selectedBank.id, bank_name: selectedBank.name });
      if (r?.redirect_url) window.location.href = r.redirect_url;
    } catch (e) {
      setMsg(e?.message || "Impossible d'initier la connexion.");
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;

  return (
    <div>
      {msg && (
        <div className={`alert ${msg.startsWith("Sync OK") ? "success" : "danger"}`} style={{ marginBottom: 12 }}>
          {msg}
        </div>
      )}
      {connections.map(c => (
        <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px",
          background: "var(--card)", border: "var(--border)", borderRadius: 10, marginBottom: 10 }}>
          <BankInitials name={c.bank_name} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{c.bank_name}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
              <StatusBadge status={c.status} />
              {c.sca_required_at && (
                <span style={{ fontSize: 11, color: "#d97706", fontWeight: 600 }}>⚠️ Re-auth SCA requise</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
              {c.sync_error_count > 0 && `${c.sync_error_count} erreur(s) • `}
              Dernière sync : {fmtDate(c.last_sync_at)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn sm" onClick={() => handleSync(c.id)} disabled={syncing === c.id}>
              {syncing === c.id ? "Sync…" : "🔄 Sync"}
            </button>
            <button className="btn sm danger" onClick={() => handleDisconnect(c.id, c.bank_name)}>Déconnecter</button>
          </div>
        </div>
      ))}
      <div onClick={() => setShowModal(true)}
        style={{ padding: "14px 18px", border: "1.5px dashed #d1d5db", borderRadius: 10,
          display: "flex", alignItems: "center", gap: 12, cursor: "pointer", color: "var(--text-muted)" }}>
        <span style={{ fontSize: 22 }}>+</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Connecter une banque via Bridge (DSP2)</div>
          <div style={{ fontSize: 12 }}>BNP, Crédit Agricole, SG, Qonto, Shine, Revolut…</div>
        </div>
      </div>
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3 className="modal-title">Connecter une banque</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="alert info" style={{ marginBottom: 16 }}>
              Connexion sécurisée via Bridge API (DSP2). Vos identifiants bancaires ne transitent jamais par nos serveurs.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
              {banks.map(b => (
                <div key={b.id} onClick={() => setSelectedBank(b)}
                  style={{ padding: "12px 8px", textAlign: "center", borderRadius: 8, cursor: "pointer",
                    border: `1.5px solid ${selectedBank?.id === b.id ? "var(--g)" : "#e5e7eb"}`,
                    background: selectedBank?.id === b.id ? "var(--gl)" : "var(--card)", fontSize: 12, fontWeight: 600 }}>
                  {b.name}
                </div>
              ))}
            </div>
            <div className="btn-group" style={{ justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn primary" onClick={handleConnect} disabled={!selectedBank}>Se connecter</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet 2 : Vue consolidée ─────────────────────────────────────
function TabConsolidee() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.banking.summary().then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;
  if (!data)   return <div className="alert danger">Impossible de charger la vue consolidée.</div>;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Solde consolidé", value: fmtEur(data.total_balance),    color: "var(--g)" },
          { label: "Non rapprochées", value: data.unreconciled_count,        color: data.unreconciled_count > 0 ? "#d97706" : "var(--text)" },
          { label: "Alertes SCA",     value: data.sca_required_count,        color: data.sca_required_count > 0 ? "#dc2626" : "var(--text)" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: "var(--card)", border: "var(--border)", borderRadius: 10, padding: "16px 20px" }}>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Par banque</div>
      {(data.by_bank || []).map(b => (
        <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px",
          background: "var(--card)", border: "var(--border)", borderRadius: 10, marginBottom: 10 }}>
          <BankInitials name={b.bank_name} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{b.bank_name}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
              {b.accounts_count} compte(s) • Sync : {fmtDate(b.last_sync_at)}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <StatusBadge status={b.status} />
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--g)", marginTop: 4 }}>{fmtEur(b.total_balance)}</div>
          </div>
        </div>
      ))}
      {(data.by_bank || []).length === 0 && (
        <div className="alert info">Aucune banque connectée. Ajoutez une banque dans l'onglet "Mes banques".</div>
      )}
    </div>
  );
}

// ── Onglet 3 : Transactions ───────────────────────────────────────
function TabTransactions() {
  const [txs, setTxs]         = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", is_reconciled: "" });
  const [page, setPage]       = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, offset: page * LIMIT };
      if (filters.date_from)            params.date_from     = filters.date_from;
      if (filters.date_to)              params.date_to       = filters.date_to;
      if (filters.is_reconciled !== "") params.is_reconciled = filters.is_reconciled;
      const r = await api.banking.transactions(params);
      setTxs(r.transactions || []);
      setTotal(r.total || 0);
    } catch { setTxs([]); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input type="date" className="form-input" style={{ width: 150 }} value={filters.date_from}
          onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        <input type="date" className="form-input" style={{ width: 150 }} value={filters.date_to}
          onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
        <select className="form-input" style={{ width: 180 }} value={filters.is_reconciled}
          onChange={e => setFilters(f => ({ ...f, is_reconciled: e.target.value }))}>
          <option value="">Toutes</option>
          <option value="true">Rapprochées</option>
          <option value="false">Non rapprochées</option>
        </select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-muted)" }}>{total} transaction(s)</span>
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>
      ) : (
        <table className="table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Date</th><th>Banque / Compte</th><th>Libellé</th>
              <th style={{ textAlign: "right" }}>Montant</th><th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {txs.map(tx => (
              <tr key={tx.id}>
                <td style={{ fontSize: 12 }}>{fmtDate(tx.date)}</td>
                <td style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 600 }}>{tx.bank_name}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: 11 }}>
                    {tx.account_name} {tx.iban_last4 ? `••••${tx.iban_last4}` : ""}
                  </div>
                </td>
                <td style={{ fontSize: 12, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {tx.description || "—"}
                </td>
                <td style={{ textAlign: "right", fontWeight: 700, fontSize: 13,
                  color: tx.amount >= 0 ? "#16a34a" : "#dc2626" }}>
                  {tx.amount >= 0 ? "+" : ""}{fmtEur(tx.amount)}
                </td>
                <td>
                  {tx.is_reconciled
                    ? <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>Rapprochée</span>
                    : <span style={{ fontSize: 11, color: "#9ca3af" }}>En attente</span>}
                </td>
              </tr>
            ))}
            {txs.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
                Aucune transaction.
              </td></tr>
            )}
          </tbody>
        </table>
      )}
      {total > LIMIT && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12 }}>
          <button className="btn sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Précédent</button>
          <span style={{ fontSize: 12, alignSelf: "center", color: "var(--text-muted)" }}>
            Page {page + 1} / {Math.ceil(total / LIMIT)}
          </span>
          <button className="btn sm" disabled={(page + 1) * LIMIT >= total} onClick={() => setPage(p => p + 1)}>Suivant</button>
        </div>
      )}
    </div>
  );
}

// ── Onglet 4 : Rapprochement ──────────────────────────────────────
function TabRapprochement() {
  const [txs, setTxs]               = useState([]);
  const [loading, setLoading]       = useState(true);
  const [autoRunning, setAutoRunning] = useState(false);
  const [msg, setMsg]               = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.banking.transactions({ is_reconciled: "false", limit: 100 });
      setTxs(r.transactions || []);
    } catch { setTxs([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAutoReconcile = async () => {
    setAutoRunning(true); setMsg("");
    try {
      const r = await api.banking.autoReconcile();
      setMsg(`Rapprochement auto : ${r.matched} rapprochée(s) sur ${r.total_checked} vérifiée(s).`);
      load();
    } catch (e) {
      setMsg(e?.message || "Erreur");
    } finally { setAutoRunning(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button className="btn primary" onClick={handleAutoReconcile} disabled={autoRunning}>
          {autoRunning ? "Analyse…" : "⚡ Rapprochement automatique"}
        </button>
        {msg && <span style={{ fontSize: 12, color: msg.includes("Erreur") ? "#dc2626" : "#16a34a" }}>{msg}</span>}
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>
      ) : (
        <table className="table" style={{ width: "100%" }}>
          <thead>
            <tr><th>Date</th><th>Banque</th><th>Libellé</th><th style={{ textAlign: "right" }}>Montant</th><th>Statut</th></tr>
          </thead>
          <tbody>
            {txs.map(tx => (
              <tr key={tx.id}>
                <td style={{ fontSize: 12 }}>{fmtDate(tx.date)}</td>
                <td style={{ fontSize: 12 }}>
                  <div>{tx.bank_name}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: 11 }}>{tx.account_name}</div>
                </td>
                <td style={{ fontSize: 12 }}>{tx.description || "—"}</td>
                <td style={{ textAlign: "right", fontWeight: 700, fontSize: 13,
                  color: tx.amount >= 0 ? "#16a34a" : "#dc2626" }}>
                  {tx.amount >= 0 ? "+" : ""}{fmtEur(tx.amount)}
                </td>
                <td><span style={{ fontSize: 11, color: "#9ca3af" }}>Non rapprochée</span></td>
              </tr>
            ))}
            {txs.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
                Toutes les transactions sont rapprochées. ✅
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Onglet 5 : Import relevé bancaire ────────────────────────────
function TabImportReleve({ currentCompanyId, onNavigate }) {
  // Traitement du relevé (valider, ignorer, rapprocher, compte, taux) : administrateur et comptable ;
  // les autres rôles consultent seulement (23/10/2026)
  const { user } = useAuth();
  const canEdit = ["admin", "comptable"].includes(user?.role);
  const fileRef                         = useRef(null);
  const [step, setStep]                 = useState("upload");
  const [filename, setFilename]         = useState("");
  const [fileContent, setFileContent]   = useState("");
  const [preview, setPreview]           = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [staging, setStaging]           = useState([]);
  const [importing, setImporting]       = useState(false);
  const [validating, setValidating]     = useState(false);
  const [msg, setMsg]                   = useState({ type: "", text: "" });
  const [imports, setImports]           = useState([]);
  // Comptes proposés sur le compte de chaque ligne : plan de la société, comptes
  // clients et fournisseurs nommés, codes sans fiche (A1a, 02/11/2026) ; saisie libre
  const [planAccounts, setPlanAccounts] = useState([]);
  const [allocRow, setAllocRow] = useState(null); // ventilation en cours (lot 2)
  const [attachRow, setAttachRow] = useState(null); // rattachement a posteriori en cours (lot 2)
  const [expenseRow, setExpenseRow] = useState(null); // remboursement de notes de frais en cours
  const [showSuggestions, setShowSuggestions] = useState(false); // propositions automatiques de factures
  const [showRemises, setShowRemises] = useState(false); // import d'une remise de virements (B1b)
  const [remiseRow, setRemiseRow] = useState(null);      // détail de la remise d'une ligne
  useEffect(() => {
    if (currentCompanyId) api.chartAccounts.choices(currentCompanyId).then(l => setPlanAccounts(Array.isArray(l) ? l : [])).catch(() => setPlanAccounts([]));
  }, [currentCompanyId]);

  // Charger l'historique — se recharge si la société change
  useEffect(() => {
    if (currentCompanyId) loadImports();
    setStep("upload");
    setFilename("");
    setFileContent("");
    setPreview(null);
    setImportResult(null);
    setMsg({ type: "", text: "" });
  }, [currentCompanyId]);

  const loadImports = async () => {
    if (!currentCompanyId) return;
    try {
      const rows = await api.bankImport.imports(currentCompanyId);
      setImports(rows || []);
    } catch {}
  };

  // Lecture du fichier — latin1 pour OFX SG
  const handleFile = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  setFilename(file.name);
  setMsg({ type: "", text: "" });
  const reader = new FileReader();
  reader.onload = (ev) => {
  const buffer = ev.target.result;
  // Convertir en Base64 — évite tous les problèmes d'encodage JSON
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  setFileContent(base64);
  setStep("upload");
  setPreview(null);
  setImportResult(null);
};
reader.readAsArrayBuffer(file);
};

  // Étape 1 — Analyze (preview sans importer)
  const handleAnalyze = async () => {
    if (!fileContent || !currentCompanyId) {
      console.log("DEBUG — fileContent vide:", !fileContent, "cid:", currentCompanyId);
      return;
    }
    setImporting(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.bankImport.analyze(currentCompanyId, { content: fileContent, filename, encoding: 'base64' });
      setPreview(r);
      setStep("preview");
    } catch (e) {
      setMsg({ type: "danger", text: e?.message || "Erreur d'analyse du fichier." });
    } finally { setImporting(false); }
  };

  // Étape 2 — Import en staging
  // Réouvrir un import existant (reprendre une validation en attente)
  const handleReopenImport = async (importId) => {
    if (!currentCompanyId) return;
    setImporting(true); setMsg({ type: '', text: '' });
    try {
      const rows = await api.bankImport.staging(currentCompanyId, { import_id: importId });
      setStaging(rows || []);
      setSelected(new Set()); setMonthFilter("");
      setImportResult({ import_id: importId });
      setStep('staging');
    } catch (e) {
      setMsg({ type: 'danger', text: e?.message || "Erreur lors de l'ouverture de l'import." });
    } finally { setImporting(false); }
  };

  // Lien « ligne du relevé » d'une facture fournisseur (Achats) : rouvre le relevé concerné
  useEffect(() => {
    let id = null;
    try { id = sessionStorage.getItem("execpro360.openImport"); sessionStorage.removeItem("execpro360.openImport"); } catch {}
    if (id && currentCompanyId) handleReopenImport(Number(id));
  }, [currentCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = async () => {
    if (!currentCompanyId) return;
    setImporting(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.bankImport.import(currentCompanyId, { content: fileContent, filename, encoding: 'base64' });
      setImportResult(r);
      const rows = await api.bankImport.staging(currentCompanyId, { import_id: r.import_id });
      setStaging(rows || []);
      setStep("staging");
      loadImports();
    } catch (e) {
      const errMsg = e?.message || "Erreur d'import.";
      if (e?.status === 409 || errMsg.includes("déjà été importé")) {
        setMsg({ type: "warning", text: `⚠️ ${errMsg}` });
      } else {
        setMsg({ type: "danger", text: errMsg });
      }
    } finally { setImporting(false); }
  };

  // Mise à jour d'une ligne staging
  const handleStagingUpdate = async (id, account, label) => {
    try {
      await api.bankImport.updateStaging(id, { user_override_account: account, category_label: label });
      setStaging(s => s.map(r => r.id === id ? { ...r, user_override_account: account, category_label: label } : r));
    } catch {}
  };

  // Ignorer une ligne — exclue de la validation, sans créer d'écriture
  // Taux de TVA d'une ligne sans facture ("" = revenir au taux par défaut du compte)
  const handleVatRate = async (id, value) => {
    const vat_rate = value === "" ? null : Number(value);
    try {
      await api.bankImport.updateStaging(id, { vat_rate });
      setStaging(s => s.map(r => r.id === id ? { ...r, vat_rate } : r));
    } catch (e) {
      setMsg({ type: "danger", text: e?.message || "Erreur lors de l'enregistrement du taux de TVA." });
    }
  };

  const handleIgnore = async (id) => {
    try {
      await api.bankImport.updateStaging(id, { status: 'rejected' });
      setStaging(s => s.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
    } catch (e) {
      setMsg({ type: "danger", text: e?.message || "Erreur lors de l'exclusion de la ligne." });
    }
  };

  // Réactiver une ligne précédemment ignorée
  const handleUnignore = async (id) => {
    try {
      await api.bankImport.updateStaging(id, { status: 'pending' });
      setStaging(s => s.map(r => r.id === id ? { ...r, status: 'pending' } : r));
    } catch (e) {
      setMsg({ type: "danger", text: e?.message || "Erreur." });
    }
  };

  // Correspondances certaines : numéro de facture lu dans le libellé
  const isCertain = (r) => r.status === "pending" && r.reconciled_invoice_id && (r.reconcile_score || 0) >= 95;
  const certainRows = staging.filter(isCertain);
  const reloadStaging = async () => {
    if (!importResult?.import_id) return;
    const rows = await api.bankImport.staging(currentCompanyId, { import_id: importResult.import_id });
    setStaging(rows);
  };
  const paymentMsg = (r) => `${r.entries_created} écriture(s) créée(s)${r.invoices_paid ? `, ${r.invoices_paid} facture(s) soldée(s) et lettrée(s)` : ""}${r.invoices_partial?.length ? `, ${r.invoices_partial.length} paiement(s) partiel(s) (facture laissée en attente du solde)` : ""}.`;
  const handleValidateCertain = async () => {
    if (!importResult?.import_id || !certainRows.length) return;
    if (!window.confirm(`${certainRows.length === 1 ? "Valider l'encaissement rapproché" : `Valider les ${certainRows.length} encaissements rapprochés`} d'une facture (numéro de facture ou suggestion acceptée) ? Les autres lignes restent en attente.`)) return;
    setValidating(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.bankImport.validate(importResult.import_id, certainRows.map(x => x.id));
      setMsg({ type: "success", text: `✅ ${paymentMsg(r)}` });
      await reloadStaging();
      loadImports();
    } catch (e) {
      setMsg({ type: "danger", text: e?.message || "Erreur de validation." });
    } finally { setValidating(false); }
  };
  // Validation ligne par ligne ou d'une sélection : mêmes contrôles que « Valider toutes »
  // (charge comptée deux fois, TVA par ligne), confirmation avant exécution
  const [selected, setSelected] = useState(() => new Set());
  // Affichage du tableau : filtre par mois (tous par défaut), tri par date croissante,
  // libellés complets (tous, ou ligne par ligne d'un clic sur le libellé)
  const [monthFilter, setMonthFilter] = useState("");
  const [fullLabels, setFullLabels] = useState(false);
  const [openLabels, setOpenLabels] = useState(() => new Set());
  const months = [...new Set(staging.map(r => String(r.date || "").slice(0, 7)).filter(Boolean))].sort();
  const visibleRows = staging.filter(r => !monthFilter || String(r.date || "").startsWith(monthFilter))
    .slice().sort((a, b) => String(a.date).localeCompare(String(b.date)) || a.id - b.id);
  const toggleLabel = (id) => setOpenLabels(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pendingRows = staging.filter(r => r.status === "pending");
  const visiblePending = visibleRows.filter(r => r.status === "pending");
  const toggleSelected = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // « Tout sélectionner » porte sur les lignes affichées (mois filtré)
  const allPendingSelected = visiblePending.length > 0 && visiblePending.every(r => selected.has(r.id));
  const selectedPending = pendingRows.filter(r => selected.has(r.id));
  const toggleAllPending = () => setSelected(s => {
    const n = new Set(s);
    for (const r of visiblePending) allPendingSelected ? n.delete(r.id) : n.add(r.id);
    return n;
  });
  const describeRow = (r) => {
    const acc = r.reconciled_invoice_id ? `facture ${r.invoice_number || "#" + r.reconciled_invoice_id}`
      : r.reconciled_purchase_invoice_id ? `facture d'achat ${r.purchase_invoice_number || "#" + r.reconciled_purchase_invoice_id}`
      : `compte ${r.user_override_account || r.category_account || "471"}`;
    const v = lineVat(r);
    const vat = v ? (v.tva > 0 ? ` (HT ${fmtEur(v.ht)} + TVA ${fmtEur(v.tva)})` : " (sans TVA déductible)") : "";
    return `• ${fmtDate(r.date)} ${r.label.slice(0, 40)} : ${fmtEur(r.amount)} → ${acc}${vat}`;
  };
  const handleValidateRows = async (rows) => {
    rows = rows.filter(r => r.status === "pending");
    if (!importResult?.import_id || !rows.length) return;
    const atRisk = rows.filter(chargeOnPaidPurchase);
    const riskText = atRisk.length
      ? `\n\n⚠️ ${atRisk.length} ligne(s) partent en compte de charge alors qu'un achat du même montant est déjà comptabilisé (risque de charge comptée deux fois) : ${atRisk.slice(0, 5).map(r => r.same_amount_purchase.number).join(", ")}${atRisk.length > 5 ? "…" : ""}.`
      : "";
    const detail = rows.slice(0, 12).map(describeRow).join("\n") + (rows.length > 12 ? `\n… et ${rows.length - 12} autre(s)` : "");
    if (!window.confirm(`${rows.length === 1 ? "Valider cette ligne" : `Valider ces ${rows.length} lignes`} et créer les écritures comptables (datées du jour de chaque opération) ?\n\n${detail}${riskText}`)) return;
    setValidating(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.bankImport.validate(importResult.import_id, rows.map(x => x.id));
      setMsg({ type: "success", text: `✅ ${paymentMsg(r)}` });
      setSelected(new Set());
      await reloadStaging();
      loadImports();
    } catch (e) {
      setMsg({ type: "danger", text: e?.message || "Erreur de validation." });
    } finally { setValidating(false); }
  };

  // Annuler la validation d'une ligne : aperçu de l'extourne et des effets, confirmation, exécution
  const handleUnvalidate = async (row) => {
    setMsg({ type: "", text: "" });
    let p;
    try { p = await api.bankImport.unvalidatePreview(row.id); }
    catch (e) { setMsg({ type: "danger", text: `Annulation impossible : ${e?.message || "erreur."}` }); return; }
    const fmtL = l => `  ${l.account_code} ${l.account_name || ""} : ${l.debit ? "débit " + fmtEur(l.debit) : "crédit " + fmtEur(l.credit)}`;
    const effects = [
      p.letterings.length ? `• Lettrage supprimé (${p.letterings.map(l => `${l.account_code} ${l.lettering_code}`).join(", ")}).` : null,
      p.invoice ? `• Facture ${p.invoice.number} : repasse « en attente » si elle n'est plus soldée.` : null,
      p.purchase ? `• Facture d'achat ${p.purchase.number} : repasse « non payée » si elle n'est plus soldée.` : null,
      p.allocations ? `• Ventilation annulée (${p.allocations} imputation(s)) : chaque facture repasse « en attente » ou « non payée » si elle n'est plus soldée ; la ligne pourra être ventilée de nouveau.` : null,
      "• La ligne du relevé repasse « En attente » (compte et rapprochement conservés).",
    ].filter(Boolean).join("\n");
    if (!window.confirm(`Annuler la validation de cette ligne (${fmtDate(row.date)}, ${fmtEur(row.amount)}) ?\n\nÉcriture d'extourne (${p.entry_ref} annulée), à la date d'origine :\n${p.reversal.map(fmtL).join("\n")}\n\n${effects}`)) return;
    setValidating(true);
    try {
      const r = await api.bankImport.unvalidate(row.id);
      setMsg({ type: "success", text: `Validation annulée : extourne ${r.entry_ref_reversal}${r.invoice_status ? `, facture ${r.invoice} repassée « ${r.invoice_status === "late" ? "en retard" : "en attente"} »` : ""}${r.purchase_status ? `, facture d'achat ${r.purchase} repassée « non payée »` : ""}.` });
      await reloadStaging();
    } catch (e) { setMsg({ type: "danger", text: `Annulation impossible : ${e?.message || "erreur."}` }); }
    finally { setValidating(false); }
  };

  // Annuler un rattachement a posteriori (lot 2) : OD de reclassement contre-passée, factures rouvertes
  const handleDetach = async (row) => {
    if (!window.confirm(`Annuler le rattachement de la ligne du ${fmtDate(row.date)} (${fmtEur(row.amount)}) ?\n\n${row.allocation_summary || ""}\n\nL'OD de reclassement éventuelle est contre-passée à sa date, le lettrage retiré, et les factures repassent « en attente » si elles ne sont plus soldées.`)) return;
    setValidating(true);
    try {
      const r = await api.bankImport.attachCancel(row.id);
      setMsg({ type: "success", text: `Rattachement annulé${r.reclass_reversed ? " (OD de reclassement contre-passée)" : ""}${r.invoices_reopened.length ? ` ; factures rouvertes : ${r.invoices_reopened.join(", ")}` : ""}.` });
      await reloadStaging();
    } catch (e) { setMsg({ type: "danger", text: `Annulation impossible : ${e?.message || "erreur."}` }); }
    finally { setValidating(false); }
  };

  // Annuler le remboursement de notes de frais porté par une ligne
  const handleExpenseCancel = async (row) => {
    if (!window.confirm(`Annuler le remboursement de notes de frais de la ligne du ${fmtDate(row.date)} (${fmtEur(row.amount)}) ?\n\nL'écriture du remboursement est extournée à sa date (la ligne revient en attente), ou l'OD de reclassement contre-passée, ou le lien seul retiré si la ligne était déjà sur le compte de remboursement. Les notes redeviennent « validées ».`)) return;
    setValidating(true);
    try {
      await api.expenses.reimburseCancel(row.id);
      setMsg({ type: "success", text: "Remboursement annulé : notes de nouveau « validées »." });
      await reloadStaging();
    } catch (e) { setMsg({ type: "danger", text: `Annulation impossible : ${e?.message || "erreur."}` }); }
    finally { setValidating(false); }
  };

  // Recalcul des suggestions : aperçu → application (confirmation) → annulation du dernier recalcul
  const [resuggest, setResuggest] = useState(null);       // aperçu en cours
  const [lastResuggest, setLastResuggest] = useState(null); // dernier recalcul (pour l'annulation)
  const loadLastResuggest = useCallback(async () => {
    if (!importResult?.import_id) return;
    try { setLastResuggest((await api.bankImport.resuggestLast(importResult.import_id)).last); } catch { setLastResuggest(null); }
  }, [importResult?.import_id]);
  useEffect(() => { if (step === "staging") loadLastResuggest(); }, [step, loadLastResuggest]);
  const handleResuggestPreview = async () => {
    if (!importResult?.import_id) return;
    setValidating(true); setMsg({ type: "", text: "" });
    try { setResuggest(await api.bankImport.resuggestPreview(importResult.import_id)); }
    catch (e) { setMsg({ type: "danger", text: e?.message || "Erreur lors du calcul de l'aperçu." }); }
    finally { setValidating(false); }
  };
  const handleResuggestApply = async () => {
    if (!importResult?.import_id || !resuggest?.changed) return;
    if (!window.confirm(`Appliquer les nouvelles suggestions à ${resuggest.changed} ligne(s) ? Rien n'est comptabilisé ; l'état actuel est enregistré et pourra être restauré par « Annuler le dernier recalcul ».`)) return;
    setValidating(true);
    try {
      const r = await api.bankImport.resuggestApply(importResult.import_id);
      setMsg({ type: "success", text: `↻ Suggestions recalculées sur ${r.changed} ligne(s). Rien n'est comptabilisé.` });
      setResuggest(null); await reloadStaging(); await loadLastResuggest();
    } catch (e) { setMsg({ type: "danger", text: e?.message || "Erreur." }); }
    finally { setValidating(false); }
  };
  const handleResuggestUndo = async () => {
    if (!importResult?.import_id) return;
    if (!window.confirm("Restaurer les suggestions telles qu'elles étaient avant le dernier recalcul ?")) return;
    setValidating(true);
    try {
      const r = await api.bankImport.resuggestUndo(importResult.import_id);
      setMsg({ type: "success", text: `Dernier recalcul annulé : ${r.restored} ligne(s) restaurée(s)${r.skipped_validated ? `, ${r.skipped_validated} ligne(s) validée(s) ou ignorée(s) entre-temps laissée(s) telles quelles` : ""}.` });
      await reloadStaging(); await loadLastResuggest();
    } catch (e) { setMsg({ type: "danger", text: e?.message || "Erreur." }); }
    finally { setValidating(false); }
  };

  // Factures validées après l'import du relevé : recalcul des rapprochements (lignes en attente seulement)
  const handleRematch = async () => {
    if (!importResult?.import_id) return;
    setValidating(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.bankImport.rematch(importResult.import_id);
      setMsg({ type: "success", text: `↻ ${r.lines} ligne(s) en attente revues : ${r.reconciled} rapprochée(s) par numéro de facture, ${r.suggested} suggestion(s) à accepter ou laisser. Rien n'est comptabilisé.` });
      await reloadStaging();
    } catch (e) { setMsg({ type: "danger", text: e?.message || "Erreur." }); }
    finally { setValidating(false); }
  };
  const handleMatch = async (row, data) => {
    try { await api.bankImport.updateStaging(row.id, data); await reloadStaging(); }
    catch (e) { setMsg({ type: "danger", text: e?.message || "Erreur." }); }
  };

  // Étape 3 — Valider → écritures comptables
  const handleValidate = async () => {
    if (!importResult?.import_id) return;
    const atRisk = staging.filter(chargeOnPaidPurchase);
    const riskText = atRisk.length
      ? `\n\n⚠️ ${atRisk.length} ligne(s) partent en compte de charge alors qu'un achat du même montant est déjà comptabilisé (risque de charge comptée deux fois) : ${atRisk.slice(0, 5).map(r => r.same_amount_purchase.number).join(", ")}${atRisk.length > 5 ? "…" : ""}.`
      : "\n\nAucune ligne avec l'avertissement « charge comptée deux fois ».";
    // Lignes sans facture rapprochée qui partent au compte d'attente 471 (à ventiler ensuite)
    const toSuspense = pendingRows.filter(r => !r.reconciled_invoice_id && !r.reconciled_purchase_invoice_id
      && String(r.user_override_account || r.category_account || "471").startsWith("471")).length;
    const suspenseText = `\n\n${toSuspense} ligne(s) partiront en 471 (compte d'attente, à ventiler ensuite).`;
    if (!window.confirm(`Valider les ${pendingRows.length} ligne(s) en attente du relevé${monthFilter ? " (tous les mois, pas seulement le mois affiché)" : ""} et créer les écritures comptables ?${suspenseText}${riskText}`)) return;
    setValidating(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.bankImport.validate(importResult.import_id);
      setMsg({ type: "success", text: `✅ ${paymentMsg(r)}` });
      setStep("done");
      loadImports();
    } catch (e) {
      setMsg({ type: "danger", text: e?.message || "Erreur de validation." });
    } finally { setValidating(false); }
  };

  const reset = () => {
    setStep("upload"); setFilename(""); setFileContent("");
    setPreview(null); setImportResult(null); setStaging([]); setSelected(new Set()); setMonthFilter("");
    setMsg({ type: "", text: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  // Barre d'actions du relevé rouvert : affichée au-dessus et au-dessous du tableau
  const actionBar = (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      <button className="btn" onClick={reset}>Nouvel import</button>
      {canEdit && (<>
      <button className="btn" onClick={handleRematch} disabled={validating} title="Pour les factures validées après l'import de ce relevé : nouveaux rapprochements et suggestions, rien n'est comptabilisé">↻ Recalculer les rapprochements</button>
      <button className="btn" onClick={() => setShowSuggestions(true)} disabled={validating} title="Factures proposées pour les encaissements pas encore reliés (lignes en attente ou déjà comptabilisées) : rien n'est enregistré avant votre confirmation">🔎 Proposer les factures</button>
      <button className="btn" onClick={() => setShowRemises(true)} disabled={validating} title="Détail d'une remise de virements fournisseurs (fichier CSV de la banque) : chaque virement relié à sa facture d'achat ; rien n'est enregistré avant votre confirmation">🧾 Remises de virements</button>
      <button className="btn" onClick={handleResuggestPreview} disabled={validating} title="Comptes suggérés recalculés avec les règles actuelles (lignes en attente, non rapprochées, sans compte ni taux choisis à la main) : aperçu avant application, rien n'est comptabilisé">↻ Recalculer les suggestions</button>
      {lastResuggest && !lastResuggest.undone_at && (
        <button className="btn" onClick={handleResuggestUndo} disabled={validating} title={`Recalcul du ${fmtDate(lastResuggest.created_at)} (${lastResuggest.changed_count} ligne(s)) : restaure les suggestions précédentes à l'identique`}>↶ Annuler le dernier recalcul</button>
      )}
      {selectedPending.length > 0 && (
        <button className="btn primary" onClick={() => handleValidateRows(selectedPending)} disabled={validating}>
          {validating ? "Validation…" : `✓ Valider la sélection (${selectedPending.length})`}
        </button>
      )}
      {certainRows.length > 0 && (
        <button className="btn primary" onClick={handleValidateCertain} disabled={validating}>
          {validating ? "Validation…" : certainRows.length === 1 ? "✓ Valider la correspondance certaine" : `✓ Valider les ${certainRows.length} correspondances certaines`}
        </button>
      )}
      <button className="btn" onClick={handleValidate} disabled={validating}>
        {validating ? "Validation…" : "⚡ Valider toutes les lignes restantes"}
      </button>
      </>)}
    </div>
  );

  if (!currentCompanyId) return (
    <div className="alert info">Sélectionnez une société dans la sidebar pour importer un relevé.</div>
  );

  return (
    <div>
      {/* ── Historique imports ── */}
      {imports.length > 0 && step === "upload" && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "var(--text-muted)" }}>
            Imports précédents
          </div>
          {imports.slice(0, 5).map(imp => (
            <div key={imp.id} onClick={() => handleReopenImport(imp.id)}
              title={imp.filename || 'Sans nom'}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", cursor: "pointer",
              background: "var(--card)", border: "var(--border)", borderRadius: 8, marginBottom: 6, fontSize: 12 }}>
              <span style={{ flex: 1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{imp.filename || "Sans nom"}</span>
              <span style={{ color: "var(--text-muted)" }}>{fmtDate(imp.period_from)} → {fmtDate(imp.period_to)}</span>
              <span style={{ color: "var(--text-muted)" }}>{imp.total_lines} lignes</span>
              <span style={{ color: "#16a34a", fontWeight: 600 }}>{imp.validated_count} validées</span>
              {imp.pending_count > 0 && (
                <span style={{ color: "#d97706", fontWeight: 600 }}>{imp.pending_count} en attente</span>
              )}
              {/* Relevé importé par erreur (03/11/2026) : administrateur, seulement si aucune ligne n'a laissé de trace */}
              {user?.role === "admin" && imp.deletable && (
                <button className="btn sm" style={{ color: "#b91c1c" }} title="Supprimer ce relevé importé par erreur (aucune ligne validée, rapprochée ni ventilée)"
                  onClick={async e => {
                    e.stopPropagation();
                    if (!window.confirm(`Supprimer le relevé « ${imp.filename || "Sans nom"} » (${imp.total_lines} lignes, ${fmtDate(imp.period_from)} → ${fmtDate(imp.period_to)}) ?\n\nAucune de ses lignes n'est comptabilisée : rien d'autre n'est modifié. Une sauvegarde est faite avant, et la suppression est inscrite au journal des actions.`)) return;
                    try {
                      const r = await api.bankImport.deleteImport(currentCompanyId, imp.id);
                      setMsg({ type: "success", text: `✅ Relevé « ${r.filename || "Sans nom"} » supprimé (${r.lines} lignes).` });
                      loadImports();
                    } catch (err) { setMsg({ type: "error", text: err.message }); }
                  }}>🗑 Supprimer ce relevé</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Zone upload ── */}
      {step === "upload" && (
        <div>
          <div style={{ border: "2px dashed #d1d5db", borderRadius: 12, padding: "40px 20px",
            textAlign: "center", cursor: "pointer", background: "var(--card)" }}
            onClick={() => fileRef.current?.click()}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
              Glissez votre relevé bancaire ici
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
              Formats acceptés : Excel (.xlsx, .xls), OFX, QIF, CSV (LCL, BNP, Qonto, Shine, Crédit Agricole, Société Générale…)
            </div>
            <button className="btn primary" onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}>
              Choisir un fichier
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.ofx,.qif,.csv,.txt"
              style={{ display: "none" }} onChange={handleFile} />
          </div>
          <div style={{ marginTop: 12, background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px", fontSize: 12.5 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Format attendu</div>
            <div>Le relevé tel qu'exporté par votre banque, sans le modifier. Le plus sûr : <strong>OFX</strong> ; Excel (.xlsx, .xls), CSV, QIF acceptés.</div>
            <div style={{ marginTop: 4 }}>Colonnes : <strong>Date</strong> · <strong>Libellé</strong> · <strong>Montant</strong> (signé) ou <strong>Débit</strong> et <strong>Crédit</strong> — avec ou sans ligne d'en-têtes. Dates JJ/MM/AAAA ; montants 1 234,56 (le signe moins pour un débit).</div>
            <div style={{ marginTop: 4 }}>Exemple : <code>05/10/2026 ; PRLV SEPA FOURNISSEUR ; -120,00</code></div>
            <div style={{ marginTop: 6 }}><strong>LCL</strong> : export OFX ou Excel recommandé ; le CSV « T_cpte… » (sans ligne d'en-têtes) est aussi accepté, ses soldes de début et de fin servent de contrôle.</div>
            <div style={{ marginTop: 2, color: "var(--text-muted)" }}>Autres banques : choisissez l'export OFX (souvent « Quicken / Money » ou « logiciel de comptabilité ») ; à défaut Excel ou CSV.</div>
          </div>
          {fileContent && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
                background: "var(--card)", border: "var(--border)", borderRadius: 8 }}>
                <span style={{ fontSize: 20 }}>📄</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{filename}</span>
                <button className="btn sm" onClick={reset}>✕ Changer</button>
                <button className="btn primary sm" onClick={handleAnalyze} disabled={importing}>
                  {importing ? "Analyse…" : "🔍 Analyser"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Preview ── */}
      {step === "preview" && preview && (
        <div>
          {/* Messages de lecture : colonne manquante, lignes écartées (numéro et motif), contrôle des soldes */}
          {preview.warning && <div style={{ background: "#fef3c7", color: "#92400e", padding: "10px 14px", borderRadius: 10, marginBottom: 10, fontSize: 13 }}>⚠️ {preview.warning}</div>}
          {preview.rejected_warning && <div style={{ background: "#fef3c7", color: "#92400e", padding: "10px 14px", borderRadius: 10, marginBottom: 10, fontSize: 13 }}>⚠️ {preview.rejected_warning}</div>}
          {preview.balance_warning && <div style={{ background: "#fef3c7", color: "#92400e", padding: "10px 14px", borderRadius: 10, marginBottom: 10, fontSize: 13 }}>⚠️ {preview.balance_warning}</div>}
          {preview.statement_balance?.ok && <div style={{ background: "#dcfce7", color: "#166534", padding: "8px 12px", borderRadius: 10, marginBottom: 10, fontSize: 12.5 }}>✓ Soldes du relevé contrôlés : {Number(preview.statement_balance.opening.amount).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € + opérations = {Number(preview.statement_balance.closing.amount).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
            {[
              { label: "Format détecté", value: (preview.format || "—").toUpperCase() },
              { label: "Transactions",   value: preview.count },
              { label: "Période",        value: `${fmtDate(preview.period?.from)} → ${fmtDate(preview.period?.to)}` },
              { label: "Net",            value: fmtEur(preview.net), color: preview.net >= 0 ? "#16a34a" : "#dc2626" },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: "var(--card)", border: "var(--border)", borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: color || "var(--text)" }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "12px 16px" }}>
              <div style={{ fontSize: 11, color: "#15803d", marginBottom: 2 }}>Total entrées</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#16a34a" }}>+{fmtEur(preview.total_in)}</div>
            </div>
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 16px" }}>
              <div style={{ fontSize: 11, color: "#991b1b", marginBottom: 2 }}>Total sorties</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#dc2626" }}>-{fmtEur(preview.total_out)}</div>
            </div>
          </div>
          {Object.keys(preview.categories || {}).length > 0 && (
            <div style={{ background: "var(--card)", border: "var(--border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Catégories détectées automatiquement</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {Object.entries(preview.categories).map(([cat, nb]) => (
                  <span key={cat} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 99,
                    background: "var(--gl)", color: "var(--g)", fontWeight: 600 }}>
                    {cat} ({nb})
                  </span>
                ))}
              </div>
            </div>
          )}
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Aperçu (5 premières lignes)</div>
          <table className="table" style={{ width: "100%", marginBottom: 20, fontSize: 12 }}>
            <thead>
              <tr><th>Date</th><th>Libellé</th><th style={{ textAlign: "right" }}>Montant</th><th>Compte auto</th></tr>
            </thead>
            <tbody>
              {(preview.sample || []).map((t, i) => (
                <tr key={i}>
                  <td>{fmtDate(t.date)}</td>
                  <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.label}</td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: t.amount >= 0 ? "#16a34a" : "#dc2626" }}>
                    {t.amount >= 0 ? "+" : ""}{fmtEur(t.amount)}
                  </td>
                  <td style={{ color: "var(--text-muted)" }}>
                    {t.account_code ? `${t.account_code} — ${t.category_label}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn" onClick={reset}>← Changer de fichier</button>
            <button className="btn primary" onClick={handleImport} disabled={importing || !preview.count}>
              {importing ? "Import en cours…" : `✅ Importer ${preview.count} transactions en staging`}
            </button>
          </div>
        </div>
      )}

      {/* ── Staging ── */}
      {step === "staging" && (
        <div>
          {importResult && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
              {[
                { label: "Importées",        value: staging.length,                                             color: "var(--text)" },
                { label: "Rapprochées (n° de facture)", value: staging.filter(r => r.reconciled_invoice_id && (r.reconcile_score || 0) >= 95).length, color: "#16a34a" },
                { label: "À traiter",        value: staging.filter(r => r.status === "pending").length,          color: staging.filter(r => r.status === "pending").length > 0 ? "#d97706" : "#16a34a" },
                { label: "Ignorées",         value: staging.filter(r => r.status === "rejected").length,         color: "#9ca3af" },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: "var(--card)", border: "var(--border)", borderRadius: 8, padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
                </div>
              ))}
            </div>
          )}
          {resuggest && (
            <div className="card" style={{ marginBottom: 16, border: "1px solid #d97706" }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Aperçu du recalcul des suggestions — rien n'est encore modifié</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
                {resuggest.lines.length} ligne(s) concernée(s) sur {resuggest.total}, dont <strong>{resuggest.changed}</strong> changent.
                {" "}Non concernées : {resuggest.excluded.validees_ou_ignorees} validée(s) ou ignorée(s), {resuggest.excluded.rapprochees} rapprochée(s), {resuggest.excluded.compte_choisi} avec un compte choisi à la main, {resuggest.excluded.taux_choisi} avec un taux choisi à la main.
              </div>
              {resuggest.changed > 0 ? (
                <table className="table" style={{ width: "100%", fontSize: 11.5, marginBottom: 10 }}>
                  <thead><tr><th>Date</th><th>Libellé</th><th style={{ textAlign: "right" }}>Montant</th><th>Actuel</th><th>Proposé</th></tr></thead>
                  <tbody>
                    {resuggest.lines.filter(l => l.changed).map(l => (
                      <tr key={l.id}>
                        <td>{fmtDate(l.date)}</td>
                        <td title={l.label} style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.label}</td>
                        <td style={{ textAlign: "right" }}>{fmtEur(l.amount)}</td>
                        <td>{l.current.account || "471"} <span style={{ color: "var(--text-muted)" }}>{l.current.label || "À ventiler"}{l.current.vat_default != null ? ` · TVA ${String(l.current.vat_default).replace(".", ",")} %` : ""}</span></td>
                        <td style={{ fontWeight: 600 }}>{l.proposed.account || "471"} <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>{l.proposed.label || "À ventiler"}{l.proposed.vat_default != null ? ` · TVA ${String(l.proposed.vat_default).replace(".", ",")} %` : ""}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div style={{ fontSize: 12, marginBottom: 10 }}>Aucune suggestion ne change.</div>}
              <div style={{ display: "flex", gap: 8 }}>
                {resuggest.changed > 0 && <button className="btn primary" onClick={handleResuggestApply} disabled={validating}>Appliquer à {resuggest.changed} ligne(s)</button>}
                <button className="btn" onClick={() => setResuggest(null)} disabled={validating}>Fermer sans rien changer</button>
              </div>
            </div>
          )}
          {/* Barre d'actions aussi au-dessus du tableau (relevés de plus de 100 lignes) */}
          <div style={{ marginBottom: 12 }}>{actionBar}</div>
          {msg.text && <div className={`alert ${msg.type}`} style={{ marginBottom: 12 }}>{msg.text}</div>}
          {canEdit && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
              💡 Vous pouvez modifier le compte comptable avant de valider.
              Les lignes avec <strong>471</strong> sont à ventiler manuellement.
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10, fontSize: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Mois
              <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)} aria-label="Filtrer par mois"
                style={{ width: "auto", fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1px solid #d1d5db", background: "var(--card)", color: "var(--text)" }}>
                <option value="">Tous ({staging.length})</option>
                {months.map(m => (
                  <option key={m} value={m}>
                    {new Date(`${m}-01T00:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })} ({staging.filter(r => String(r.date || "").startsWith(m)).length})
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={fullLabels} onChange={e => setFullLabels(e.target.checked)} />
              Libellés complets
            </label>
            <span style={{ color: "var(--text-muted)" }}>Triées par date · cliquer sur un libellé l'affiche en entier</span>
          </div>
          {!canEdit && (
            <div className="alert info" style={{ marginBottom: 10, fontSize: 12.5 }}>Consultation seule : le traitement du relevé (comptes, taux, rapprochements, validation) est réservé à l'administrateur et au comptable.</div>
          )}
          <table className="table" style={{ width: "100%", fontSize: 12, marginBottom: 20 }}>
            <thead>
              <tr>
                <th style={{ width: 24 }}>
                  {canEdit && <input type="checkbox" checked={allPendingSelected} disabled={!visiblePending.length} onChange={toggleAllPending}
                    aria-label="Sélectionner les lignes en attente affichées" title="Sélectionner les lignes en attente affichées" />}
                </th>
                <th>Date</th><th>Libellé</th>
                <th style={{ textAlign: "right" }}>Montant</th>
                <th>Compte</th><th>Rapprochement</th><th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(row => (
                <tr key={row.id} style={{
                  background: row.status === "rejected" ? "#f9fafb" : row.reconciled_invoice_id ? "#f0fdf4" : "inherit",
                  opacity: row.status === "rejected" ? 0.5 : 1,
                }}>
                  <td>
                    {canEdit && row.status === "pending" && (
                      <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleSelected(row.id)} aria-label="Sélectionner la ligne" />
                    )}
                  </td>
                  <td>{fmtDate(row.date)}</td>
                  <td title={row.label} onClick={() => toggleLabel(row.id)}
                    style={fullLabels || openLabels.has(row.id)
                      ? { maxWidth: 320, whiteSpace: "normal", wordBreak: "break-word", cursor: "pointer" }
                      : { maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                    {row.label}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700,
                    color: row.amount >= 0 ? "#16a34a" : "#dc2626" }}>
                    {row.amount >= 0 ? "+" : ""}{fmtEur(row.amount)}
                  </td>
                  <td>
                    {(() => {
                      const code = row.user_override_account || row.category_account || "471";
                      const known = planAccounts.find(a => String(a.account_code).toUpperCase() === String(code).toUpperCase());
                      return (
                        <>
                          {/* Enregistré seulement si la valeur change : un simple passage dans le
                              champ ne doit ni figer la suggestion ni apprendre de règle */}
                          {canEdit ? <AccountPicker key={`${row.id}-${code}`} value={code} choices={planAccounts} width={78} ariaLabel="Compte de la ligne"
                            {...bankLineGroups(row.amount >= 0 ? "credit" : "debit")}
                            onCommit={v => handleStagingUpdate(row.id, v, row.category_label)} />
                            : <strong style={{ fontSize: 11 }}>{code}</strong>}
                          {(!canEdit || !known) && (
                            <span style={{ fontSize: 10, color: "var(--text-muted)", marginLeft: 4 }}>
                              {known ? `— ${known.label}` : (row.category_label || "À ventiler")}
                            </span>
                          )}
                        </>
                      );
                    })()}
                    {lineVat(row) && (() => {
                      const v = lineVat(row);
                      return (
                        <div style={{ fontSize: 10.5, color: "var(--text-muted)", marginTop: 3, maxWidth: 260 }}>
                          TVA{" "}
                          <select value={row.vat_rate == null ? "" : String(row.vat_rate)} onChange={e => handleVatRate(row.id, e.target.value)} disabled={!canEdit}
                            aria-label="Taux de TVA de la ligne" title={v.note || undefined}
                            style={{ width: "auto", display: "inline-block", fontSize: 10.5, padding: "1px 2px", borderRadius: 4, border: "1px solid #d1d5db", background: "var(--card)", color: "var(--text)" }}>
                            <option value="">{`${String(v.def).replace(".", ",")} % (défaut du compte)`}</option>
                            {BANK_VAT.rates.map(r => <option key={r} value={String(r)}>{`${String(r).replace(".", ",")} %`}</option>)}
                          </select>
                          <div>
                            {v.tva > 0
                              ? <>HT {fmtEur(v.ht)} + TVA {fmtEur(v.tva)} ({v.vatAccount})</>
                              : <>Sans TVA déductible</>}
                          </div>
                        </div>
                      );
                    })()}
                    {chargeOnPaidPurchase(row) && (
                      <div style={{ fontSize: 10.5, color: "#b45309", marginTop: 3, maxWidth: 260, whiteSpace: "normal" }}>
                        ⚠️ Achat {row.same_amount_purchase.number} du {fmtDate(row.same_amount_purchase.date)}{row.same_amount_purchase.supplier_name ? ` (${row.same_amount_purchase.supplier_name})` : ""} déjà comptabilisé, même montant : en compte de charge, la charge serait comptée deux fois. Utilisez le compte du fournisseur (401…), ou acceptez la suggestion de rapprochement si elle est proposée.
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {row.reconciled_invoice_id ? (
                      <span style={{ color: "#16a34a", fontWeight: 600 }}>
                        ✅ <DocBadge kind="vente" />{row.invoice_number || `Facture #${row.reconciled_invoice_id}`}{row.client_name ? ` · ${row.client_name}` : ""}
                        {row.match_type === "ref_partial" && <span style={{ color: "#b45309", fontWeight: 500 }}> — paiement partiel</span>}
                        {canEdit && row.status === "pending" && <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} onClick={() => handleMatch(row, { clear_reconciliation: true })}>Retirer</button>}
                      </span>
                    ) : row.reconciled_purchase_invoice_id ? (
                      <span style={{ color: "#16a34a", fontWeight: 600 }}>✅ <DocBadge kind="achat" />{row.purchase_invoice_number}{row.supplier_name ? ` · ${row.supplier_name}` : ""}</span>
                    ) : row.reconciled_expense_ids ? (
                      <span style={{ color: "#16a34a", fontWeight: 600 }}>✅ Remboursement notes de frais {(() => { try { return JSON.parse(row.reconciled_expense_ids).map(id => `NF-${id}`).join(", "); } catch { return ""; } })()}</span>
                    ) : ((row.suggested_invoice_id || row.suggested_purchase_invoice_id) && row.status === "pending" && /_before/.test(row.match_type || "")) ? (
                      <span style={{ color: "#b45309" }}>
                        ⚠️ À vérifier : paiement antérieur à la facture <DocBadge kind={row.suggested_invoice_number ? "vente" : "achat"} />{row.suggested_invoice_number || row.suggested_purchase_number}{(row.suggested_client_name || row.suggested_supplier_name) ? ` · ${row.suggested_client_name || row.suggested_supplier_name}` : ""}{suggestionGap(row)}
                        {canEdit && <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} title="Seulement si ce paiement règle bien cette facture (par exemple un acompte versé avant la facture)" onClick={() => handleMatch(row, { accept_suggestion: true })}>C'est bien elle</button>}
                      </span>
                    ) : (row.suggested_invoice_id || row.suggested_purchase_invoice_id) && row.status === "pending" ? (
                      <span style={{ color: "#6b7280" }}>
                        💡 Suggestion : <DocBadge kind={row.suggested_invoice_number ? "vente" : "achat"} />{row.suggested_invoice_number || row.suggested_purchase_number}{(row.suggested_client_name || row.suggested_supplier_name) ? ` · ${row.suggested_client_name || row.suggested_supplier_name}` : ""}
                        {row.match_type === "ref_overpaid" ? " (montant supérieur au reste dû)" : ` (montant proche${suggestionGap(row) || ", même date"})`}
                        {canEdit && <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} onClick={() => handleMatch(row, { accept_suggestion: true })}>Accepter</button>}
                      </span>
                    ) : row.remise_transfers ? (
                      <span style={{ color: "#16a34a", fontWeight: 600 }} title={row.allocation_summary || ""}>✅ Remise : {row.remise_transfers} virement(s)
                        <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} onClick={() => setRemiseRow(row)}>Détail</button>
                      </span>
                    ) : row.allocation_summary ? (
                      <span style={{ color: "#16a34a", fontWeight: 600 }} title={row.allocation_summary}>✅ {row.allocation_kind === "rattachement" ? "Rattachée" : "Ventilée"} : {row.allocation_summary}</span>
                    ) : row.match_type === "ref_multiple" && row.status === "pending" ? (
                      <span style={{ color: "#b45309" }}>Plusieurs factures citées : à ventiler
                        {canEdit && <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} onClick={() => setAllocRow(row)}>Ventiler</button>}
                      </span>
                    ) : (
                      <span style={{ color: "#9ca3af" }}>—</span>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, fontWeight: 600,
                      background: row.status === "validated" ? "#dcfce7" : row.status === "rejected" ? "#f3f4f6" : "#fef3c7",
                      color: row.status === "validated" ? "#16a34a" : row.status === "rejected" ? "#9ca3af" : "#d97706" }}>
                      {row.status === "validated" ? "Validée" : row.status === "rejected" ? "Ignorée" : "En attente"}
                    </span>
                    {canEdit && row.status === "pending" && (
                      <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px", fontWeight: 600 }} disabled={validating}
                        onClick={() => handleValidateRows([row])}
                        title="Crée l'écriture comptable de cette ligne seule, à sa date (confirmation avant exécution ; administrateur et comptable).">
                        Valider
                      </button>
                    )}
                    {canEdit && row.status === "pending" && !row.reconciled_invoice_id && !row.reconciled_purchase_invoice_id && (
                      <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} disabled={validating}
                        onClick={() => setAllocRow(row)}
                        title={row.amount > 0 ? "Répartir cet encaissement sur plusieurs factures de vente (paiement partiel, commission retenue)." : "Répartir ce paiement sur plusieurs factures d'achat (paiement partiel, frais en reliquat)."}>
                        Ventiler
                      </button>
                    )}
                    {canEdit && row.amount < 0 && ["pending", "validated"].includes(row.status) && !row.reconciled_invoice_id && !row.reconciled_purchase_invoice_id && !row.reconciled_expense_ids && !row.allocation_kind && (
                      <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} disabled={validating}
                        onClick={() => setExpenseRow(row)}
                        title="Constater sur cette ligne le remboursement d'une ou plusieurs notes de frais validées (virement groupé compris) : une seule écriture de banque par ligne.">
                        🧾 Notes de frais…
                      </button>
                    )}
                    {canEdit && row.status === "validated" && row.reconciled_expense_ids && (
                      <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} disabled={validating}
                        onClick={() => handleExpenseCancel(row)}
                        title="Défait le remboursement des notes de frais porté par cette ligne.">
                        Annuler le remboursement
                      </button>
                    )}
                    {canEdit && row.status === "pending" && (
                      <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }}
                        onClick={() => handleIgnore(row.id)}
                        title="Exclut cette ligne de la validation : aucune écriture comptable ne sera créée. À utiliser pour les mouvements déjà comptabilisés ailleurs (ex : CCA, prêts) ou à traiter manuellement.">
                        Ignorer
                      </button>
                    )}
                    {canEdit && row.status === "validated" && !row.reconciled_invoice_id && !row.reconciled_purchase_invoice_id && !row.reconciled_expense_ids && (!row.allocation_kind || row.has_tiers_allocation) && (
                      <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} disabled={validating}
                        onClick={() => setAttachRow(row)}
                        title="Relier après coup cette ligne validée à ses factures : sans nouvelle écriture si elle est sur le compte du client ou du fournisseur, par une OD de reclassement si elle est sur un compte de produit (70…) ou d'attente (471).">
                        Rattacher…
                      </button>
                    )}
                    {canEdit && row.status === "validated" && row.allocation_kind === "rattachement" && (
                      <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} disabled={validating}
                        onClick={() => handleDetach(row)}
                        title="Défait le rattachement : l'OD de reclassement éventuelle est contre-passée, les factures repassent « en attente ».">
                        Annuler le rattachement
                      </button>
                    )}
                    {canEdit && row.status === "validated" && row.allocation_kind !== "rattachement" && !row.reconciled_expense_ids && (
                      <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }} disabled={validating}
                        onClick={() => handleUnvalidate(row)}
                        title="Extourne l'écriture à sa date d'origine, remet la ligne en attente et délettre la facture rapprochée (administrateur et comptable). Impossible si la période est clôturée ou la TVA déclarée.">
                        Annuler la validation
                      </button>
                    )}
                    {canEdit && row.status === "rejected" && (
                      <button className="btn sm" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px" }}
                        onClick={() => handleUnignore(row.id)}
                        title="Réintègre cette ligne dans le lot à valider.">
                        Annuler
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {actionBar}
          {showRemises && (
            <BankRemisesModal onClose={() => setShowRemises(false)}
              onDone={async (r) => { setMsg({ type: r.refused ? "error" : "success", text: `${r.confirmed} remise(s) importée(s)${r.refused ? `, ${r.refused} refusée(s) (motif dans la fenêtre)` : ""}.` }); await reloadStaging(); }} />
          )}
          {remiseRow && <BankRemiseDetail row={remiseRow} onClose={() => setRemiseRow(null)} />}
          {showSuggestions && (
            <BankSuggestionsModal importId={importResult?.import_id || null} onClose={() => setShowSuggestions(false)} onNavigate={onNavigate}
              onDone={async (r) => { setMsg({ type: "success", text: `✅ ${r.confirmed} mouvement(s) relié(s) à leurs factures${r.refused ? `, ${r.refused} refusé(s) (motif dans la fenêtre)` : ""}.` }); await reloadStaging(); }} />
          )}
          {expenseRow && (
            <ExpenseReimburseModal row={expenseRow} companyId={currentCompanyId} onClose={() => setExpenseRow(null)}
              onDone={async (r) => {
                setExpenseRow(null);
                setMsg({ type: "success", text: `✅ Remboursement constaté : ${r.notes.map(n => n.piece).join(", ")} (${fmtEur(r.total)})${r.entry_ref ? ` — écriture ${r.entry_ref}` : " — sans nouvelle écriture"}.` });
                await reloadStaging();
              }} />
          )}
          {attachRow && (
            <BankAttachModal row={attachRow} onClose={() => setAttachRow(null)}
              onDone={async (r) => {
                setAttachRow(null);
                const due = r.results.filter(x => x.remaining > 0.005);
                setMsg({ type: "success", text: `✅ Ligne rattachée${r.mode === "reclassement" ? ` (OD de reclassement ${r.entry_ref} depuis ${r.account})` : " sans nouvelle écriture"} : ${r.items.map(i => i.number).join(", ")}${due.length ? ` ; reste dû : ${due.map(x => `${x.number} ${Number(x.remaining).toFixed(2).replace(".", ",")} €`).join(", ")}` : ""}${r.left_on_account > 0.005 ? ` ; ${Number(r.left_on_account).toFixed(2).replace(".", ",")} € restent sur ${r.account}` : ""}.` });
                await reloadStaging();
              }} />
          )}
          {allocRow && (
            <BankAllocationModal row={allocRow} planAccounts={planAccounts} onClose={() => setAllocRow(null)}
              onDone={async (r) => {
                setAllocRow(null);
                const due = r.results.filter(x => x.remaining > 0.005);
                setMsg({ type: "success", text: `✅ Ligne ventilée (${r.entry_ref}) : ${r.items.length} facture(s)${due.length ? ` ; reste dû : ${due.map(x => `${x.number} ${Number(x.remaining).toFixed(2).replace(".", ",")} €`).join(", ")}` : ", toutes soldées"}${r.remainder ? ` ; reliquat ${Number(r.remainder.amount).toFixed(2).replace(".", ",")} € sur ${r.remainder.account}` : ""}.` });
                await reloadStaging();
              }} />
          )}
        </div>
      )}

      {/* ── Done ── */}
      {step === "done" && (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Import validé avec succès</div>
          {msg.text && <div style={{ fontSize: 14, color: "#16a34a", marginBottom: 24 }}>{msg.text}</div>}
          <button className="btn primary" onClick={reset}>Importer un autre relevé</button>
        </div>
      )}

      {/* Message global */}
      {msg.text && step !== "done" && (
        <div className={`alert ${msg.type}`} style={{ marginTop: 16 }}>{msg.text}</div>
      )}
    </div>
  );
}

// ── Onglet 6 : Alertes & SCA ─────────────────────────────────────
function TabAlertes() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.banking.summary().then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;

  const scaConns = (data?.by_bank || []).filter(b => b.sca_required_at);
  const errConns = (data?.by_bank || []).filter(b => b.status === "erreur" || b.sync_error_count >= 3);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Re-authentification SCA (DSP2 — 90 jours)</div>
        {scaConns.length === 0 ? (
          <div className="alert success">Aucune re-authentification SCA requise.</div>
        ) : scaConns.map(c => (
          <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px",
            background: "#fef3c7", border: "1.5px solid #d97706", borderRadius: 10, marginBottom: 10 }}>
            <BankInitials name={c.bank_name} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{c.bank_name}</div>
              <div style={{ fontSize: 12, color: "#92400e", marginTop: 2 }}>
                Re-authentification requise depuis le {fmtDate(c.sca_required_at)}.
              </div>
            </div>
            <StatusBadge status="action_requise" />
          </div>
        ))}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Connexions en erreur</div>
        {errConns.length === 0 ? (
          <div className="alert success">Toutes les connexions sont opérationnelles.</div>
        ) : errConns.map(c => (
          <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px",
            background: "#fee2e2", border: "1.5px solid #dc2626", borderRadius: 10, marginBottom: 10 }}>
            <BankInitials name={c.bank_name} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{c.bank_name}</div>
              <div style={{ fontSize: 12, color: "#991b1b", marginTop: 2 }}>
                {c.sync_error_count} erreur(s).{c.sync_error_count >= 5 && " Sync auto désactivée."}
              </div>
            </div>
            <StatusBadge status={c.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────
export default function Banque({ currentCompanyId, onNavigate }) {
  const [tab, setTab]             = useState(IMPORT_TAB);
  const [reloadKey, setReloadKey] = useState(0);
  const [hasBank, setHasBank]     = useState(false); // au moins une banque connectée
  const reload = () => setReloadKey(k => k + 1);
  useEffect(() => {
    api.banking.connections().then(r => setHasBank((r?.total || r?.connections?.length || 0) > 0)).catch(() => setHasBank(false));
  }, [currentCompanyId, reloadKey]);
  const visibleTabs = TABS.map((t, i) => i).filter(i => hasBank || !CONNECTED_TABS.includes(i));
  const shown = visibleTabs.includes(tab) ? tab : IMPORT_TAB;

  if (!currentCompanyId) return (
    <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>
      Chargement de la société…
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Comptes bancaires</h2>
        {/* Guide « Traiter son relevé bancaire » (page Aide) */}
        <button className="btn sm" title="Guide pas à pas : importer, rapprocher, choisir comptes et TVA, valider"
          onClick={() => { try { sessionStorage.setItem("execpro360.guide", "releve-bancaire"); } catch {} onNavigate?.("aide"); }}>
          📘 Guide
        </button>
      </div>
      <div style={{ display: "flex", gap: 2, borderBottom: "var(--border)", marginBottom: 24 }}>
        {visibleTabs.map(i => (
          <button key={i} onClick={() => setTab(i)}
            style={{
              padding: "9px 18px", fontSize: 13, fontWeight: shown === i ? 700 : 500,
              background: "none", border: "none",
              borderBottom: shown === i ? "2.5px solid var(--accent)" : "2.5px solid transparent",
              color: shown === i ? "var(--accent)" : "var(--text-muted)", cursor: "pointer",
            }}>
            {TABS[i]}
          </button>
        ))}
      </div>
      {shown === 0 && <TabMesBanques key={reloadKey} reload={reload} />}
      {shown === 1 && <TabConsolidee key={reloadKey} />}
      {shown === 2 && <TabTransactions key={reloadKey} />}
      {shown === 3 && <TabRapprochement key={reloadKey} />}
      {shown === 4 && <TabImportReleve key={`${reloadKey}-${currentCompanyId}`} currentCompanyId={currentCompanyId} onNavigate={onNavigate} />}
      {shown === 5 && <TabAlertes key={reloadKey} />}
    </div>
  );
}